import React from 'react';
import ReactECharts from 'echarts-for-react';
import { useEmotionData } from './EmotionData';

const EmotionCandlestick = ({ artworkId }) => {
  const { rawData, loading, error } = useEmotionData(artworkId);

  if (loading) {
    return <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-white">Loading emotion data...</div>;
  }

  if (error) {
    return <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-red-400">{error}</div>;
  }

  if (!rawData || rawData.length === 0) {
    return <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800 flex items-center justify-center h-80 text-center text-gray-300">No emotion data available for this artwork</div>;
  }

  // Process data for candlestick format using real session data
  // Group sessions by date and calculate open, close, low, high
  const processedData = rawData.reduce((acc, curr, index) => {
    const sessionTime = curr.Session_Start_time || curr.session_start_time;
    if (!sessionTime) return acc;
    
    // Parse date - handle formats like "DD/MM/YYYY" or "DD/MM/YYYY HH:mm:ss"
    let day, month, year;
    if (sessionTime.includes('/')) {
      const parts = sessionTime.split(/[/\s]/);
      day = parts[0];
      month = parts[1];
      year = parts[2];
    } else {
      // Fallback for other formats
      const date = new Date(sessionTime);
      day = date.getDate();
      month = date.getMonth() + 1;
      year = date.getFullYear();
    }
    
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    if (!acc[formattedDate]) {
      acc[formattedDate] = {
        sessions: [],
        date: formattedDate
      };
    }
    
    const duration = curr.Session_Duration || curr.session_duration || 0;
    
    // Parse time from sessionTime if available (format: "DD/MM/YYYY HH:mm:ss")
    let timeStr = '';
    if (sessionTime.includes(' ')) {
      timeStr = sessionTime.split(' ')[1] || '';
    } else {
      // Use a timestamp or index for sorting
      timeStr = `${index}`;
    }
    
    // Store session with all needed data
    acc[formattedDate].sessions.push({
      duration: duration,
      time: timeStr,
      originalTime: sessionTime,
      index: index, // Preserve original order as fallback
      emotion: (curr.Emotion || curr.emotion || '').toLowerCase()
    });
    return acc;
  }, {});

  // Convert to candlestick format: [open, close, low, high]
  const candlestickData = Object.values(processedData)
    .sort((a, b) => new Date(a.date) - new Date(b.date))
    .map(day => {
      // Sort sessions by time (chronological order) to get first (open) and last (close)
      const sortedByTime = [...day.sessions].sort((a, b) => {
        // Convert time string "HH:mm:ss" to seconds for comparison
        const timeToSeconds = (timeStr) => {
          if (!timeStr || timeStr === '') return 0;
          const parts = timeStr.split(':');
          if (parts.length >= 3) {
            return parseInt(parts[0]) * 3600 + parseInt(parts[1]) * 60 + parseInt(parts[2]);
          }
          return 0;
        };
        
        const timeA = timeToSeconds(a.time);
        const timeB = timeToSeconds(b.time);
        
        // If both have valid times, compare by time
        if (timeA > 0 && timeB > 0) {
          return timeA - timeB;
        }
        
        // Otherwise use original index (preserve order from rawData)
        return a.index - b.index;
      });
      
      const durations = day.sessions.map(s => s.duration).filter(d => d > 0);
      if (durations.length === 0) {
        return null;
      }
      
      const open = sortedByTime[0]?.duration || durations[0] || 0;
      const close = sortedByTime[sortedByTime.length - 1]?.duration || durations[durations.length - 1] || 0;
      const low = Math.min(...durations);
      const high = Math.max(...durations);
      
      // Count emotions for the day
      const emotionCounts = {
        positive: day.sessions.filter(s => s.emotion === 'positive').length,
        neutral: day.sessions.filter(s => s.emotion === 'neutral').length,
        negative: day.sessions.filter(s => s.emotion === 'negative').length
      };
      
      // Determine dominant emotion
      const dominantEmotion = Object.entries(emotionCounts)
        .sort((a, b) => b[1] - a[1])[0]?.[0] || 'neutral';
      
      return {
        date: day.date,
        open: open,
        close: close,
        low: low,
        high: high,
        count: day.sessions.length,
        emotions: emotionCounts,
        dominantEmotion: dominantEmotion
      };
    })
    .filter(day => day !== null && day.count > 0); // Filter out null values and days with no valid data

  const dates = candlestickData.map(item => item.date);
  // ECharts candlestick format: [open, close, low, high]
  const data = candlestickData.map(item => [
    Math.round(item.open * 100) / 100,   // open
    Math.round(item.close * 100) / 100,  // close
    Math.round(item.low * 100) / 100,    // low
    Math.round(item.high * 100) / 100    // high
  ]);

  // 3-color gradients for candlestick (positive/negative)
  const positiveGradient = {
    type: 'linear',
    x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: '#10b981' },   // Emerald Green
      { offset: 0.5, color: '#34d399' },  // Bright Emerald
      { offset: 1, color: '#6ee7b7' }     // Light Emerald
    ]
  };

  const negativeGradient = {
    type: 'linear',
    x: 0, y: 0, x2: 0, y2: 1,
    colorStops: [
      { offset: 0, color: '#ef4444' },   // Red
      { offset: 0.5, color: '#f87171' },  // Bright Red
      { offset: 1, color: '#fca5a5' }     // Light Red
    ]
  };

  const option = {
    title: {
      text: 'Min/Max On Days',
      left: 'center',
      bottom: '3%',
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold',
        color: '#ffffff'
      }
    },
    tooltip: {
      trigger: 'axis',
      // axisPointer: {
      //   type: 'cross',
      //   crossStyle: {
      //     color: '#888888'
      //   },
      //   lineStyle: {
      //     color: '#888888',
      //     width: 2,
      //     type: 'dashed'
      //   }
      // },
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      borderColor: 'rgba(255, 255, 255, 0.3)',
      borderWidth: 1,
      textStyle: {
        color: '#fff',
        fontSize: 14
      },
      padding: [10, 15],
      formatter: function(params) {
        const param = params[0];
        const index = param.dataIndex;
        const dayData = candlestickData[index];
        if (!dayData) return '';
        
        const open = dayData.open.toFixed(2);
        const close = dayData.close.toFixed(2);
        const low = dayData.low.toFixed(2);
        const high = dayData.high.toFixed(2);
        const isRising = dayData.close >= dayData.open;
        
        const emotionColor = {
          positive: '#10b981',
          neutral: '#f59e0b',
          negative: '#ef4444'
        }[dayData.dominantEmotion] || '#999';
        
        return `
          <div style="font-weight: bold; margin-bottom: 8px; font-size: 16px;">Date: ${param.name}</div>
          <div style="margin-bottom: 6px;">
            <span style="color: ${isRising ? '#10b981' : '#ef4444'}; font-weight: bold;">${dayData.count} Sessions</span>
          </div>
          <div style="margin-bottom: 4px;">Low: <span style="color: #999; font-weight: bold;">${low}s</span></div>
          <div style="margin-bottom: 4px;">High: <span style="color: #999; font-weight: bold;">${high}s</span></div>
          <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid rgba(255,255,255,0.2);">
            <div>Dominant: <span style="color: ${emotionColor}; font-weight: bold;">${dayData.dominantEmotion.toUpperCase()}</span></div>
            <div style="font-size: 11px; margin-top: 4px; opacity: 0.9;">
              😊 ${dayData.emotions.positive} | 😐 ${dayData.emotions.neutral} | 😞 ${dayData.emotions.negative}
            </div>
          </div>
        `;
      }
    },
    xAxis: {
      type: 'category',
      data: dates,
      axisLabel: {
        rotate: -45,
        fontSize: 12,
        fontWeight: 'bold',
        color: '#e0e0e0',
        margin: 12
      },
      axisLine: {
        lineStyle: {
          color: '#666666',
          width: 2
        }
      },
      axisTick: {
        alignWithLabel: true,
        lineStyle: {
          color: '#666666'
        }
      },
      boundaryGap: true
    },
    yAxis: {
      type: 'value',
      scale: true,
      name: 'Duration (s)',
      nameLocation: 'middle',
      nameGap: 50,
      nameTextStyle: {
        fontSize: 14,
        fontWeight: 'bold',
        color: '#e0e0e0'
      },
      axisLabel: {
        formatter: '{value}s',
        fontSize: 13,
        fontWeight: 'bold',
        color: '#e0e0e0'
      },
      axisLine: {
        lineStyle: {
          color: '#666666',
          width: 2
        }
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          color: '#444444',
          width: 1
        }
      }
    },
    series: [
      {
        name: 'Session Duration',
        type: 'candlestick',
        data: data,
        itemStyle: {
          color: positiveGradient,
          color0: negativeGradient,
          borderColor: '#ef4444',
          borderColor0: '#10b981',
          borderWidth: 2,
          shadowBlur: 15,
          shadowColor: 'rgba(16, 185, 129, 0.7)',
          shadowOffsetX: 0,
          shadowOffsetY: 3
        },
        emphasis: {
          itemStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#34d399' },
                { offset: 0.5, color: '#10b981' },
                { offset: 1, color: '#059669' }
              ]
            },
            color0: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: '#f87171' },
                { offset: 0.5, color: '#ef4444' },
                { offset: 1, color: '#dc2626' }
              ]
            },
            borderColor: '#ef4444',
            borderColor0: '#10b981',
            borderWidth: 3,
            shadowBlur: 25,
            shadowColor: 'rgba(16, 185, 129, 0.9)'
          }
        }
      }
    ],
    grid: {
      left: '10%',
      right: '10%',
      bottom: '18%',
      top: '5%',
      containLabel: true
    }
  };

  return (
    <div className="w-full max-w-3xl bg-gray-950 rounded-xl p-4 shadow-2xl shadow-black/50 backdrop-blur-sm border border-gray-800">
      <div className="h-80">
        <ReactECharts option={option} style={{ height: '100%', width: '100%' }} />
      </div>
    </div>
  );
};

export default EmotionCandlestick;